import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetNotificationsQuery,
  useGetUnreadNotificationsQuery,
  useMarkNotificationAsReadMutation,
} from 'rtk/query/apis/notifications';
import { toast } from 'sonner';
import type { Notification } from 'types/notification.types';

// Define the type for the context state
interface NotificationsContextType {
  popOverisOpen?: boolean;
  setPopOverIsOpen: React.Dispatch<React.SetStateAction<boolean>>;

  unReadNotifications: Notification[];
  refetchUnreadNotifications: () => void;
  isGetUnreadNotificationsBusy: boolean;
  isGetUnreadNotificationsError: boolean;
  getUnreadNotificationsError: any;

  notifications: Notification[];
  isGetNotificationsBusy: boolean;
  refetchNotifications: () => void;

  refetchAll: () => void;

  markNotificationAsRead: (notification: Notification) => void;
  isMarkNotificationAsReadRequestLoading: boolean;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

interface NotificationsContextProviderProps {
  children: ReactNode;
}

export const NotificationsContextProvider = ({ children }: NotificationsContextProviderProps) => {
  const { t } = useTranslation('Notifications');
  // const user = useUser()

  const [popOverisOpen, setPopOverIsOpen] = useState<boolean>(false);
  const [unReadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const unReadNotificationsQuery = useGetUnreadNotificationsQuery(undefined, {
    // skip: !user,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });
  const [isGetUnreadNotificationsBusy, setIsGetUnreadNotificationsBusy] = useState<boolean>(
    unReadNotificationsQuery.isLoading || unReadNotificationsQuery.isFetching,
  );

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const getNotificationsQuery = useGetNotificationsQuery(undefined, {
    // skip: !user,
    refetchOnReconnect: true,
    refetchOnMountOrArgChange: true,
  });
  const [isGetNotificationsBusy, setIsGetNotificationsBusy] = useState<boolean>(
    getNotificationsQuery.isLoading || getNotificationsQuery.isFetching,
  );

  // Unread notifications
  useEffect(() => {
    if (!unReadNotificationsQuery) return;
    if (unReadNotificationsQuery.data && unReadNotificationsQuery.data.notifications) {
      setUnreadNotifications(unReadNotificationsQuery.data.notifications);
    }
    setIsGetUnreadNotificationsBusy(unReadNotificationsQuery.isLoading || unReadNotificationsQuery.isFetching);
  }, [unReadNotificationsQuery]);

  // All notifications
  useEffect(() => {
    if (!getNotificationsQuery) return;
    if (getNotificationsQuery.data && getNotificationsQuery.data.notifications) {
      setNotifications(getNotificationsQuery.data.notifications);
    }
    setIsGetNotificationsBusy(getNotificationsQuery.isLoading || getNotificationsQuery.isFetching);
  }, [getNotificationsQuery]);

  // Functions and mutations
  const refetchUnreadNotifications = () => {
    unReadNotificationsQuery.refetch();
  };
  const refetchNotifications = () => {
    getNotificationsQuery.refetch();
  };
  const refetchAll = () => {
    unReadNotificationsQuery.refetch();
    getNotificationsQuery.refetch();
  };
  const [markNotificationAsReadRequest, { isLoading: isMarkNotificationAsReadRequestLoading }] =
    useMarkNotificationAsReadMutation();

  const markNotificationAsRead = (notification: Notification) => {
    markNotificationAsReadRequest({ notification })
      .unwrap()
      .then((result) => {
        console.log('postCommentRequest result', result);
        refetchUnreadNotifications();
        refetchNotifications();
      })
      .catch(() => {
        toast.error(t('toast.markAsReadFailed'));
      });
  };

  const contextValue = {
    popOverisOpen,
    setPopOverIsOpen,

    unReadNotifications,
    isGetUnreadNotificationsBusy,
    refetchUnreadNotifications,

    notifications,
    isGetNotificationsBusy,
    isGetUnreadNotificationsError: unReadNotificationsQuery.isError,
    getUnreadNotificationsError: unReadNotificationsQuery.error,
    refetchNotifications,

    refetchAll,

    markNotificationAsRead,
    isMarkNotificationAsReadRequestLoading,
  };

  // if (getNotificationsQuery.isError) {
  //   return <FullViewError title='Error loading your notifications.' error={getNotificationsQuery.error} />
  // }

  return <NotificationsContext.Provider value={contextValue}>{children}</NotificationsContext.Provider>;
};

// Custom hook to use the context
export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsContextProvider');
  }
  return context;
};
